import { CASCADE_TABLE_ITEM_DRAGGED, ERRORS, INDENTATION_WIDTH } from '@/lib/constants';
import { accessPolicyType } from 'shared/constants.json';
import { computed, ref } from 'vue';
import { datadogRum } from '@datadog/browser-rum';
import { getCascadeOperation } from '@/lib/view-sort';
import { getNewParents } from '@/lib/goal/realign';

export default function useCascadeDragAndDrop(
  rootListAfter,
  rootListBefore,
  goalDetailPageListAfter,
  goalDetailPageListBefore,
  selectSingle,
  changeParents,
  viewsService,
  actionValidator,
  goalTree,
  expandSvc,
  multiAlignGoals,
) {
  const dragOverBottom = ref([]);
  const dragOverTop = ref([]);
  const dragOver = ref([]);
  const dragItem = ref(null);

  const initialLeftPosition = ref(0);

  const dragDepth = ref(0);

  const dragItemFull = computed(() => goalTree.value.find((g) => g.index === dragItem.value));

  const correctDragDepth = (over, anchor) => {
    if (over.length > 0) {
      return anchor.indentation + 1;
    }
    return dragDepth.value;
  };

  const setDragDepth = (anchor, event, dragTarget) => {
    if (dragItemFull.value === undefined) {
      return;
    }

    if (dragItemFull.value.entity.isParent) {
      return;
    }

    if (anchor === undefined) {
      return;
    }

    if (dragTarget.dragOver === true) {
      dragDepth.value = anchor.indentation + 1;
      return;
    }

    if (anchor.isFirstItem && dragTarget.dragOverTop === true) {
      dragDepth.value = anchor.indentation;
      return;
    }

    const d = Math.floor((event.screenX - initialLeftPosition.value) / INDENTATION_WIDTH);
    if (d < anchor.nextIndentation) {
      dragDepth.value = anchor.nextIndentation;
      return;
    }

    dragDepth.value = Math.min(d, anchor.indentation);
  };

  const resetDragging = () => {
    initialLeftPosition.value = 0;
    dragDepth.value = 0;
    dragOverBottom.value = [];
    dragOverTop.value = [];
    dragOver.value = [];
    dragItem.value = null;
  };

  const setDragItem = (itemId, left) => {
    dragItem.value = itemId;
    initialLeftPosition.value = left;
  };

  const sendActionToDatadog = (operation, setDragOver) => {
    datadogRum.addAction(CASCADE_TABLE_ITEM_DRAGGED, { sort: !setDragOver, realign: operation.toRealignIds.length > 0 });
  };

  const dropItem = () => {
    const anchorIndex = [...dragOverBottom.value, ...dragOverTop.value, ...dragOver.value][0];
    const dragItemIndex = dragItem.value;
    const anchor = goalTree.value.find((g) => g.index === anchorIndex);
    if (anchor === undefined) {
      resetDragging();
      return new Promise((resolve) => { resolve(); });
    }

    const correctedDragDepth = correctDragDepth(dragOver.value, anchor);
    const operation = getCascadeOperation([dragItem.value], anchorIndex, correctedDragDepth);
    const setDragOver = dragOver.value.length > 0;
    const insertAfter = dragOverBottom.value.length > 0;

    sendActionToDatadog(operation, setDragOver);
    resetDragging();
    if (setDragOver) {
      const goal = selectSingle(operation.toMoveIds[0]);
      if (goal === undefined) {
        throw new Error(`cannot find goal with id: ${operation.toMoveIds[0]}`);
      }
      const parents = getNewParents(goal, dragItemIndex, operation.newParentId, multiAlignGoals);
      return changeParents({ goals: [goal], parents });
    }

    if (operation.toRealignIds.length === 0 && correctedDragDepth === 0) {
      return sortTopLevelGoals(operation, insertAfter);
    }

    if (operation.toRealignIds.length === 0 && correctedDragDepth > 0) {
      return sortGoalChildren(operation, insertAfter);
    }

    const goal = selectSingle(operation.toMoveIds[0]);
    if (goal === undefined) {
      throw new Error(`cannot find goal with id: ${operation.toMoveIds[0]}`);
    }

    const parents = getNewParents(goal, dragItemIndex, operation.newParentId, multiAlignGoals);
    return changeParents({
      goals: [goal],
      parents,
    }).then(() => {
      if (parents.length > 0 && multiAlignGoals && operation.newParentId === null) {
        return new Promise((resolve) => { resolve(); });
      }

      if (correctedDragDepth === 0) {
        return sortTopLevelGoals(operation, insertAfter);
      }

      return sortGoalChildren(operation, insertAfter);
    });
  };

  const sortTopLevelGoals = (operation, insertAfter) => {
    if (!viewsService.canEditCurrentView.value) {
      return new Promise((resolve) => { resolve({ info: { message: ERRORS.SORT.VIEW_NOT_EDITABLE, entity: viewsService.currentView.value } }); });
    }
    const list = goalTree.value.filter((g) => g.indentation === 0).map((g) => g.entity.uid);
    if (insertAfter) {
      rootListAfter(viewsService.currentView.value, list, operation.anchorId, operation.toMoveIds);
      return new Promise((resolve) => { resolve(); });
    }
    rootListBefore(viewsService.currentView.value, list, operation.anchorId, operation.toMoveIds);
    return new Promise((resolve) => { resolve(); });
  };

  const sortGoalChildren = (operation, insertAfter) => {
    const parent = selectSingle(operation.newParentId);
    if (parent === undefined) {
      throw new Error('cannot find parent in store');
    }
    if (![accessPolicyType.full, accessPolicyType.write].includes(parent.accessRight)) {
      return new Promise((resolve) => { resolve({ info: { message: ERRORS.SORT.PARENT_NOT_EDITABLE, entity: parent } }); });
    }
    const list = parent.children.map((c) => c.uid);
    if (insertAfter) {
      goalDetailPageListAfter(parent, list, operation.anchorId, operation.toMoveIds);
      return new Promise((resolve) => { resolve(); });
    }
    goalDetailPageListBefore(parent, list, operation.anchorId, operation.toMoveIds);
    return new Promise((resolve) => { resolve(); });
  };

  const setDragOverBottom = (keys, event) => {
    if (keys.length === 0) {
      dragOverBottom.value = [];
      return;
    }

    const newAnchor = goalTree.value.find((g) => g.index === keys[0]);
    if (newAnchor === undefined) {
      dragOverBottom.value = [];
      return;
    }

    if (dragItemFull.value === undefined || (dragItemFull.value.entity.isParent && (newAnchor.nextIndentation !== 0)) || (expandSvc.getExpand(newAnchor).value && newAnchor.entity.children.length > 0)) {
      dragOverBottom.value = [];
      return;
    }

    setDragDepth(newAnchor, event, { dragOverBottom: true });
    const operation = getCascadeOperation([dragItem.value], keys[0], dragDepth.value);
    const { isValid } = actionValidator.isValid(operation);
    if (!isValid) {
      dragOverBottom.value = [];
      return;
    }

    dragOverBottom.value = keys;
  };

  const setDragOverTop = (keys, event) => {
    if (keys.length === 0) {
      dragOverTop.value = [];
      return;
    }

    const newAnchor = goalTree.value.find((g) => g.index === keys[0]);
    if (newAnchor === undefined) {
      dragOverTop.value = [];
      return;
    }

    if (dragItemFull.value === undefined || (dragItemFull.value.entity.isParent && (newAnchor.indentation !== 0)) || !newAnchor.isFirstItem) {
      dragOverTop.value = [];
      return;
    }

    setDragDepth(newAnchor, event, { dragOverTop: true });
    const operation = getCascadeOperation([dragItem.value], keys[0], dragDepth.value);
    const { isValid } = actionValidator.isValid(operation);
    if (!isValid) {
      dragOverTop.value = [];
      return;
    }

    dragOverTop.value = keys;
  };

  const setDragOver = (keys, event) => {
    if (keys.length === 0) {
      dragOver.value = [];
      return;
    }
    const newAnchor = goalTree.value.find((g) => g.index === keys[0]);
    if (newAnchor === undefined) {
      dragOverTop.value = [];
      return;
    }
    // if is not expanded or does not have children
    if (dragItemFull.value === undefined || dragItemFull.value.entity.isParent || (expandSvc.getExpand(newAnchor).value && newAnchor.entity.children.length > 0)) {
      dragOver.value = [];
      return;
    }

    setDragDepth(newAnchor, event, { dragOver: true });
    const { isValid } = actionValidator.isValid(getCascadeOperation([dragItem.value], keys[0], dragDepth.value));
    if (!isValid) {
      dragOver.value = [];
      return;
    }

    dragOver.value = keys;
  };

  return {
    dragItem,
    dropItem,
    dragDepth,
    dragOverBottom,
    dragOverTop,
    setDragItem,
    setDragOver,
    dragOver,
    setDragOverBottom,
    setDragOverTop,
    resetDragging,
  };
}
